import { useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { LazyLoadImage } from 'react-lazy-load-image-component'

export interface GalleryImage {
	alt: string
	source: string
	featured: string
	thumb: string
}

interface GalleryGridProps {
	images: GalleryImage[]
	title?: string
}

const CDN_BASE_URL = 'https://www.secretsfl.com'

export default function GalleryGrid({ images, title }: GalleryGridProps) {
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

	if (!images || images.length === 0) {
		return null
	}

	const getFullUrl = (path: string) => {
		if (path.startsWith('http')) return path
		return `${CDN_BASE_URL}${path}`
	}

	const openLightbox = (index: number) => {
		setSelectedIndex(index)
	}

	const closeLightbox = () => {
		setSelectedIndex(null)
	}

	const goToPrevious = () => {
		if (selectedIndex === null) return
		setSelectedIndex(selectedIndex === 0 ? images.length - 1 : selectedIndex - 1)
	}

	const goToNext = () => {
		if (selectedIndex === null) return
		setSelectedIndex(selectedIndex === images.length - 1 ? 0 : selectedIndex + 1)
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowLeft') goToPrevious()
		if (e.key === 'ArrowRight') goToNext()
		if (e.key === 'Escape') closeLightbox()
	}

	const currentImage = selectedIndex !== null ? images[selectedIndex] : null

	return (
		<div className="mt-6">
			{title && (
				<h3 className="text-[#ffffff] font-semibold text-lg mb-4">{title}</h3>
			)}

			{/* Thumbnail Grid */}
			<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
				{images.map((image, index) => (
					<button
						key={index}
						type="button"
						onClick={() => openLightbox(index)}
						className="overflow-hidden rounded-lg min-h-[100px] bg-[#2F2F32] hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#EF4444]"
					>
						<LazyLoadImage
							src={getFullUrl(image.thumb)}
							alt={image.alt}
							effect="opacity"
							wrapperClassName="lazy-image-wrapper"
							className="
							w-full h-full object-cover
							transition-transform duration-300
							group-hover:scale-105
							"
						/>
					</button>
				))}
			</div>

			{/* Lightbox Modal */}
			<Dialog open={selectedIndex !== null} onOpenChange={closeLightbox}>
				<DialogContent
					className="max-w-[95vw] max-h-[95vh] h-auto p-0 bg-black/95 border-0 overflow-hidden !w-full"
					onKeyDown={handleKeyDown}
				>
					{currentImage && (
						<div className="relative flex items-center justify-center w-full h-full min-h-[50vh]">
							{/* Close Button */}
							<button
								type="button"
								onClick={closeLightbox}
								className="absolute top-4 right-4 z-10 h-10 w-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
								aria-label="Close lightbox"
							>
								<X className="h-6 w-6 text-white" />
							</button>

							{/* Previous Button */}
							{images.length > 1 && (
								<button
									type="button"
									onClick={goToPrevious}
									className="absolute left-2 sm:left-4 z-10 h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
									aria-label="Previous image"
								>
									<ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
								</button>
							)}

							{/* Image */}
							<img
								src={getFullUrl(currentImage.source)}
								alt={currentImage.alt}
								className="max-w-full max-h-[85vh] object-contain"
							/>

							{/* Next Button */}
							{images.length > 1 && (
								<button
									type="button"
									onClick={goToNext}
									className="absolute right-2 sm:right-4 z-10 h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
									aria-label="Next image"
								>
									<ChevronRight className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
								</button>
							)}

							{/* Image Counter */}
							{images.length > 1 && (
								<div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full">
									<span className="text-white text-sm">
										{(selectedIndex ?? 0) + 1} / {images.length}
									</span>
								</div>
							)}

							{/* Image Alt Text */}
							{currentImage.alt && (
								<div className="absolute bottom-14 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-lg max-w-[80%]">
									<p className="text-white text-sm text-center truncate">
										{currentImage.alt}
									</p>
								</div>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	)
}
